import {
  ActionItems,
  Description,
  Detail,
  Divider,
  EditObsnIcon,
  ObsTextArea,
  Price,
  PriceDiscount,
  PriceWrapper,
  TextAreaWrapper,
  Title,
  TopItems,
  Wrapper,
  IsProdActiveTitle,
} from './ProductDetail.styled';
import { ChangeEvent, useEffect, useState } from 'react';
import type { DishAttrType, ProductType } from '../../types/product';

import { AddDishToCart } from '../Cart';
import { AttributeSelector } from './AttributeSelector';
import { PhotoGallery } from '../../components/PhotoGallery';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import type { state } from '../../types/store/state';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';

export const ProductDetail = (props: ProductType) => {
  const { t } = useTranslation();
  const cart = useSelector(({ cart }: state) => cart);
  const isProductActive = props.product.active;

  const [itemsOnCart, setItemsOnCart] = useState(0);
  const [dishAttributes, setDishAttributes] = useState<DishAttrType>({});
  const [observation, setObservation] = useState('');
  const [hardReset, setHardReset] = useState(false);

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    setObservation(event.target.value);
  };

  useEffect(() => {
    if (cart.branchStores.length) {
      const cartItems = cart.branchStores[0].products.filter(
        (item: ProductType) => item.product.storeBranchProductId === props.id
      );
      const items = cartItems.length ? cartItems[0].quantity : 0;
      setItemsOnCart(items);
    }
  }, [cart.productsCount, cart.orderCost?.productsCost]);

  const hasDishAttributes =
    props.product.hasOwnProperty('dishAttributes') &&
    props.product.dishAttributes;

  return (
    <Wrapper>
      <PhotoGallery photos={props.product.images} />
      <Detail>
        <Title>{props.product.name}
          {!isProductActive
            ? <IsProdActiveTitle>{t('product.notAvailable.title')}</IsProdActiveTitle>
            : ''}
        </Title>
        <PriceWrapper>
          {props.product.discountPercent > 0 && (
            <PriceDiscount>
              {props.product.currency.symbol}
              {props.product.fullPrice}
            </PriceDiscount>
          )}
          <Price>
            {props.product.currency.symbol}
            {props.product.price.toFixed(2)}
          </Price>
        </PriceWrapper>
        {hasDishAttributes && <Divider />}
        {!hardReset && hasDishAttributes
          ? Object.keys(props.product.dishAttributes).map((attr: string) => (
            <AttributeSelector
              key={attr}
              attribute={attr}
              dishAttributes={dishAttributes}
              setDishAttributes={setDishAttributes}
              attributesList={props.product.dishAttributes}
              currencySymbol={props.product.currency.symbol}
              setHardReset={setHardReset}
            />
          ))
          : null}
        <Divider />
        <Description>{props.product.description}</Description>
        <TextAreaWrapper>
          <TopItems>
            <EditObsnIcon icon={faPencilAlt} />
            {t('product.detail.observations.textarea.title')}
          </TopItems>
          <ObsTextArea
            name="observationNote"
            placeholder={t('product.detail.observations.textarea.placeholder')}
            value={observation}
            onChange={onChange}
          />
        </TextAreaWrapper>
        <ActionItems>
          <AddDishToCart
            dishAttributes={dishAttributes}
            storeBranchProductId={props.product.storeBranchProductId}
            customQuantity={itemsOnCart}
            added={itemsOnCart > 0}
            isDish={props.product.isDish}
            observation={observation}
            setObservation={setObservation}
            setDishAttributes={setDishAttributes}
            attributes={props.product.dishAttributes}
            disabled={!isProductActive}
            productName={props.product.name}
          />
        </ActionItems>
      </Detail>
    </Wrapper>
  );
};
